import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import PolicyIcon from '@mui/icons-material/Policy';
import GroupsIcon from '@mui/icons-material/Groups';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import ChildCareIcon from '@mui/icons-material/ChildCare';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import config from '../config';
import RuleScreen from '../shared/RuleScreen';
import FilterBar from '../shared/FilterBar';
import StatTile from '../shared/StatTile';
import DataTable from '../shared/DataTable';
import Pagination, { normalizePagination } from '../shared/Pagination';
import DetailDrawer from '../shared/DetailDrawer';
import { buildParams } from '../shared/apiParams';

const API_URL = config.apiUrl;
const EMPTY = { blocks: [], gramPanchayats: [], villages: [], enumerators: [], startDate: '', endDate: '' };

const formatDate = (date) => {
  if (!date) return 'N/A';
  const d = new Date(date);
  if (isNaN(d.getTime())) return 'Invalid Date';
  const p = (n) => n.toString().padStart(2, '0');
  return `${p(d.getMonth() + 1)}/${p(d.getDate())}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
};
const calcDuration = (start, end) => {
  if (!start || !end) return 'N/A';
  const ms = new Date(end) - new Date(start);
  if (isNaN(ms)) return 'Invalid';
  const min = Math.abs(ms / 60000);
  const h = Math.floor(min / 60);
  const m = Math.floor(min % 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const TABS = [
  { key: 'enumerator', label: 'By Enumerator', col: 'Enumerator ID' },
  { key: 'block', label: 'By Block', col: 'Block Name' },
  { key: 'gp', label: 'By Gram Panchayat', col: 'Gram Panchayat' },
  { key: 'village', label: 'By Village', col: 'Village Name' },
];

// Keys must match the backend METRICS keys (controllers/welfareGapController.js).
const METRICS = [
  { key: 'welfareGap', label: 'Welfare Gap Families', icon: PolicyIcon },
  { key: 'eligible', label: 'Eligible Households', icon: CheckCircleIcon },
  { key: 'beneficiary', label: 'Beneficiary Households', icon: VolunteerActivismIcon },
  { key: 'rationGap', label: 'Ration Card, Irregular Supply', icon: ReceiptLongIcon },
  { key: 'pmayGap', label: 'Kutcha House, No PMAY', icon: HomeWorkIcon },
  { key: 'nutritionGap', label: 'Anganwadi, No Nutrition', icon: ChildCareIcon },
];

const pctCell = (b, highlight) => (
  <>
    {b?.count || 0}
    <span style={{ fontSize: '0.72rem', color: highlight ? 'var(--danger)' : 'var(--muted)', fontWeight: highlight ? 700 : 400, marginLeft: 4 }}>
      ({b?.pct || 0}%)
    </span>
  </>
);

export default function FamilyWelfareGap() {
  const [activeTab, setActiveTab] = useState('enumerator');
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState(normalizePagination({}));
  const [applied, setApplied] = useState(EMPTY);
  const [drawer, setDrawer] = useState({ open: false, row: null, metric: null, records: [], loading: false });
  const [recordPopup, setRecordPopup] = useState({ open: false, record: null });

  const tab = TABS.find((t) => t.key === activeTab);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = buildParams(applied, { extra: { page, limit: pageSize } });
      if (activeTab !== 'enumerator') params.append('forceGroupBy', activeTab);
      const res = await axios.get(`${API_URL}/welfare-gap/analytics?${params}`);
      if (res.data?.success) {
        setRows(res.data.data || []);
        setSummary(res.data.summary || null);
        setPagination(normalizePagination(res.data.pagination || {}));
      }
    } catch (err) {
      console.error('Error fetching welfare gap data:', err);
    } finally {
      setLoading(false);
    }
  }, [activeTab, applied, page, pageSize]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const totalHouseholds = summary?.totalHouseholds ?? rows.reduce((s, r) => s + (Number(r.totalHouseholds) || 0), 0);

  const tiles = (
    <>
      <StatTile label="Total Households" value={totalHouseholds} icon={GroupsIcon} accent="var(--primary)" />
      {METRICS.map((m) => (
        <StatTile
          key={m.key}
          label={m.label}
          value={`${summary?.[m.key]?.pct ?? 0}%`}
          icon={m.icon}
          accent={m.key === 'welfareGap' ? 'var(--danger)' : 'var(--info)'}
          sub={`${summary?.[m.key]?.count ?? 0} of ${totalHouseholds} households`}
        />
      ))}
    </>
  );

  const openDrilldown = async (row, metric = null) => {
    setDrawer({ open: true, row, metric, records: [], loading: true });
    try {
      const params = buildParams(applied, {
        extra: { groupBy: activeTab, groupValue: row.name, filter: metric?.key, limit: 100 },
      });
      const res = await axios.get(`${API_URL}/welfare-gap/details?${params}`);
      const records = res.data?.data || [];
      setDrawer({ open: true, row, metric, records, loading: false });
    } catch (err) {
      console.error('Error fetching welfare gap detail records:', err);
      setDrawer({ open: true, row, metric, records: [], loading: false });
    }
  };

  const columns = [
    { key: 'first', label: tab.col, render: (r) => <span className="cell-strong">{r.name || 'N/A'}</span> },
    { key: 'totalHouseholds', label: 'Total Household', align: 'center', render: (r) => <span className="cell-strong">{r.totalHouseholds || 0}</span> },
    ...METRICS.map((m) => ({
      key: m.key,
      label: m.label,
      align: 'center',
      render: (r) => (
        <span className="clickable-name" onClick={(e) => { e.stopPropagation(); openDrilldown(r, m); }}>
          {pctCell(r[m.key], m.key === 'welfareGap')}
        </span>
      ),
    })),
  ];

  const drawerRecordColumns = [
    { key: 'srNo', label: 'Sr No', align: 'center', render: (_r, i) => i + 1 },
    { key: 'household_id', label: 'Household ID', render: (r) => r.household_id || '—' },
    { key: 'respondent_name', label: 'Respondent', render: (r) => r.respondent_name || '—' },
    { key: 'rationCard', label: 'Ration Card', render: (r) => r.rationCard || '—' },
    { key: 'rationRegular', label: 'Ration Regular', render: (r) => r.rationRegular || '—' },
    { key: 'houseType', label: 'House Type', render: (r) => r.houseType || '—' },
    { key: 'pmayBenefit', label: 'PMAY Benefit', render: (r) => r.pmayBenefit || '—' },
    { key: 'anganwadiRegistered', label: 'Anganwadi Registered', render: (r) => r.anganwadiRegistered || '—' },
    { key: 'nutritionReceived', label: 'Nutrition Received', render: (r) => r.nutritionReceived || '—' },
    { key: 'eligibleCount', label: 'Eligible Count', align: 'center', render: (r) => r.eligibleCount ?? '—' },
    { key: 'beneficiaryCount', label: 'Beneficiary Count', align: 'center', render: (r) => r.beneficiaryCount ?? '—' },
    { key: 'block', label: 'Block', render: (r) => r.block || '—' },
    { key: 'village_name', label: 'Village', render: (r) => r.village_name || '—' },
    { key: 'start', label: 'Start Time', render: (r) => formatDate(r.start) },
    { key: 'end', label: 'End Time', render: (r) => formatDate(r.end) },
    { key: 'duration', label: 'Duration', align: 'center', render: (r) => calcDuration(r.start, r.end) },
  ];

  const filterBar = (
    <FilterBar
      onApply={(v) => { setApplied(v); setPage(1); }}
      onClear={() => { setApplied(EMPTY); setPage(1); }}
      loading={loading}
    />
  );

  return (
    <RuleScreen
      title="Welfare Gap Families"
      subtitle="Eligible but not receiving schemes — households where the eligible count is greater than the beneficiary count, and the beneficiary count is greater than zero. Ration, housing, and nutrition gaps are shown as reference columns."
      icon={PolicyIcon}
      backTo="/family-dashboard/segmentation-framework"
      backLabel="Back to Segmentation Framework"
      tiles={tiles}
      filterBar={
        <>
          <div className="tabs">
            {TABS.map((t) => (
              <button
                key={t.key}
                className={`tab-btn${activeTab === t.key ? ' active' : ''}`}
                onClick={() => { if (t.key !== activeTab) { setActiveTab(t.key); setPage(1); } }}
              >
                {t.label}
              </button>
            ))}
          </div>
          {filterBar}
        </>
      }
    >
      <div className="card">
        <h3 className="card-title">Welfare Gap Families by {tab.label.replace('By ', '')}</h3>
        <Pagination
          pagination={pagination}
          onPageChange={setPage}
          pageSize={pageSize}
          onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
          unitLabel="groups"
          shown={rows.length}
        />
        <DataTable
          columns={columns}
          rows={rows}
          loading={loading}
          emptyMessage='No data found. Adjust filters and click "Apply Filters".'
          onRowClick={(r) => openDrilldown(r, null)}
          getRowKey={(r, i) => `${r.name}-${i}`}
        />
      </div>

      <DetailDrawer
        open={drawer.open}
        onClose={() => setDrawer((d) => ({ ...d, open: false }))}
        title={drawer.row ? `${tab.col}: ${drawer.row.name} — ${drawer.metric ? drawer.metric.label : 'All Households'}` : ''}
        sections={
          drawer.row
            ? [
                {
                  title: 'Breakdown',
                  items: [
                    { label: 'Total Households', value: drawer.row.totalHouseholds || 0 },
                    ...METRICS.map((m) => ({
                      label: m.label,
                      value: `${drawer.row[m.key]?.count || 0} (${drawer.row[m.key]?.pct || 0}%)`,
                    })),
                  ],
                },
              ]
            : []
        }
      >
        <div className="detail-section">
          <h4>Households</h4>
          <DataTable
            columns={drawerRecordColumns}
            rows={drawer.records}
            loading={drawer.loading}
            emptyMessage="No households found."
            onRowClick={(r) => setRecordPopup({ open: true, record: r })}
            getRowKey={(r, i) => r.uuid || i}
          />
        </div>
      </DetailDrawer>

      <DetailDrawer
        open={recordPopup.open}
        onClose={() => setRecordPopup((p) => ({ ...p, open: false }))}
        title={recordPopup.record ? `Household: ${recordPopup.record.household_id}` : ''}
        sections={
          recordPopup.record
            ? [
                {
                  title: 'Status',
                  items: [
                    {
                      label: 'Welfare Gap Family',
                      value: (
                        <span className={`badge ${recordPopup.record.isWelfareGap ? 'badge-danger' : 'badge-success'}`}>
                          {recordPopup.record.isWelfareGap ? 'Yes' : 'No'}
                        </span>
                      ),
                    },
                  ],
                },
                {
                  title: 'Household',
                  items: [
                    { label: 'Household ID', value: recordPopup.record.household_id },
                    { label: 'Respondent', value: recordPopup.record.respondent_name },
                    { label: 'Ration Card', value: recordPopup.record.rationCard },
                    { label: 'Ration Received Regularly', value: recordPopup.record.rationRegular },
                    { label: 'House Type', value: recordPopup.record.houseType },
                    { label: 'PMAY Benefit', value: recordPopup.record.pmayBenefit },
                    { label: 'Anganwadi Registered', value: recordPopup.record.anganwadiRegistered },
                    { label: 'Supplementary Nutrition', value: recordPopup.record.nutritionReceived },
                    { label: 'Eligible Count', value: recordPopup.record.eligibleCount },
                    { label: 'Beneficiary Count', value: recordPopup.record.beneficiaryCount },
                    { label: 'Enumerator', value: recordPopup.record.enumerator_id },
                    { label: 'Block', value: recordPopup.record.block },
                    { label: 'Gram Panchayat', value: recordPopup.record.gram_panchayat },
                    { label: 'Village', value: recordPopup.record.village_name },
                  ],
                },
                {
                  title: 'Survey Timing',
                  items: [
                    { label: 'Start Time', value: formatDate(recordPopup.record.start) },
                    { label: 'End Time', value: formatDate(recordPopup.record.end) },
                    { label: 'Duration', value: calcDuration(recordPopup.record.start, recordPopup.record.end) },
                  ],
                },
              ]
            : []
        }
      />
    </RuleScreen>
  );
}
